import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, User, Users, ArrowLeft } from 'lucide-react';
import { Particles } from '@/components/ui/particles';
import PersonaLevelCard from './PersonaLevelCard';
import { isLearnerKipPlayable, catalogStatusLabel } from '@/lib/catalogAvailability';
import type { CatalogAvailability, ClientProfile, Colleague, Kip } from '@/types';

interface PersonaLevel {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  completed: boolean;
  locked: boolean;
  stars: number;
  kipId: string;
  category: string;
  availability: CatalogAvailability;
  colleagueId: string | null;
  guardianId: string | null;
}

interface PersonaWorldViewProps {
  world: {
    id: string;
    name: string;
    age: number;
    diagnosis: string;
    description: string;
    color: string;
    gradient: string;
    icon: React.ReactNode;
    client: ClientProfile | null;
    colleague: Colleague | null;
    levels: PersonaLevel[];
    completed: number;
    total: number;
    type: 'client' | 'colleague' | 'general';
    catalogStatus: CatalogAvailability;
  };
  onBack: () => void;
  isAdmin: boolean;
  clients: ClientProfile[];
  colleagues: Colleague[];
}

const PersonaWorldView: React.FC<PersonaWorldViewProps> = ({
  world,
  onBack,
  isAdmin,
  clients,
  colleagues,
}) => {
  const navigate = useNavigate();

  const avatarUrl =
    world.client?.avatar ||
    world.colleague?.avatar ||
    'https://images.pexels.com/photos/1462637/pexels-photo-1462637.jpeg?auto=compress&cs=tinysrgb&w=400';

  const progressPct = world.total > 0 ? (world.completed / world.total) * 100 : 0;

  const teaserBanner = world.catalogStatus === 'coming_soon' || world.catalogStatus === 'draft';

  const startLevel = (level: PersonaLevel) => {
    const minimal: Kip = {
      id: level.kipId,
      title: level.title,
      description: level.description,
      category: level.category as Kip['category'],
      duration: level.duration,
      availability: level.availability,
      client: world.client,
      colleagueId: level.colleagueId,
      guardianId: level.guardianId,
      agentId: null,
    };
    if (!isLearnerKipPlayable(minimal, clients, colleagues, isAdmin)) return;
    navigate(`/player/${level.kipId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className={`-mx-6 -mt-8 relative overflow-hidden bg-gradient-to-br ${world.color} text-white`}>
        <Particles
          className="absolute inset-0"
          quantity={40}
          ease={70}
          color="#ffffff"
          size={0.4}
          staticity={40}
        />

        <div className="absolute -top-24 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-10">
          <button
            onClick={onBack}
            className="group flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            <span className="text-sm font-medium">Back to Personas</span>
          </button>

          <div className="flex items-start gap-5 mb-7">
            <div className="relative flex-shrink-0">
              <img
                src={avatarUrl}
                alt={world.name}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-white/30 shadow-2xl"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                {world.client ? (
                  <User className="w-3.5 h-3.5 text-white" />
                ) : (
                  <Users className="w-3.5 h-3.5 text-white" />
                )}
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">{world.name}</h1>
                {teaserBanner && (
                  <span className="text-xs font-semibold uppercase tracking-wide bg-white/20 border border-white/30 px-2.5 py-1 rounded-full">
                    {catalogStatusLabel(world.catalogStatus)}
                  </span>
                )}
              </div>

              {world.client ? (
                <p className="text-white/65 text-sm mb-2">
                  {world.age} years old&nbsp;&nbsp;·&nbsp;&nbsp;{world.diagnosis}
                </p>
              ) : world.colleague ? (
                <p className="text-white/65 text-sm mb-2">
                  {world.colleague.title}&nbsp;&nbsp;·&nbsp;&nbsp;{world.colleague.organization}
                </p>
              ) : null}

              <p className="text-white/80 text-sm leading-relaxed max-w-xl">{world.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Trophy className="w-4 h-4 text-yellow-300 flex-shrink-0" />
            <span className="text-white/80 text-sm font-medium">
              {world.completed}/{world.total} completed
            </span>
            <div className="w-32 bg-white/20 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-white/80 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {teaserBanner && (
        <div className="max-w-4xl mx-auto px-6 pt-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-950 px-4 py-3 text-sm">
            {world.catalogStatus === 'draft'
              ? 'This persona is a draft in the catalog. Learners will not see it until it is marked available or coming soon.'
              : 'This persona is not open for live training yet. You can preview scenarios below; start buttons unlock when the experience is ready.'}
          </div>
        </div>
      )}

      {world.client ? (
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Client Profile
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Learning Style</h3>
                <p className="text-sm text-slate-600">{world.client.style}</p>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Key Strengths</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  {world.client.strengths.slice(0, 2).map((strength: string, i: number) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-500 mr-1">•</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Current Targets</h3>
                <div className="flex flex-wrap gap-1">
                  {world.client.currentTargets.slice(0, 3).map((target: string, i: number) => (
                    <span key={i} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                      {target}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Reinforcers</h3>
                <div className="flex flex-wrap gap-1">
                  {world.client.reinforcers.slice(0, 4).map((reinforcer: string, i: number) => (
                    <span key={i} className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                      {reinforcer}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : world.colleague ? (
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Professional Profile
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Specialty</h3>
                <p className="text-sm text-slate-600">{world.colleague.specialty}</p>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Communication Style</h3>
                <p className="text-sm text-slate-600">{world.colleague.communicationStyle}</p>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Areas of Expertise</h3>
                <div className="flex flex-wrap gap-1">
                  {world.colleague.expertise.slice(0, 4).map((skill: string, i: number) => (
                    <span key={i} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-medium text-slate-700 mb-2">Background</h3>
                <p className="text-sm text-slate-600">{world.colleague.backgroundInfo}</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="max-w-4xl mx-auto px-6 pb-10">
        <div className="space-y-4">
          {world.levels.map((level) => (
            <PersonaLevelCard key={level.id} level={level} onStart={() => startLevel(level)} isAdmin={isAdmin} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PersonaWorldView;
